import { usePermissions } from '../hooks/usePermissions';

/**
 * A wrapper component that conditionally renders its children based on permissions.
 *
 * @param {Object} props
 * @param {string|string[]} [props.permission] - A single permission string or array of strings.
 *                                               If an array is passed, defaults to requiring ANY of them unless requireAll is true.
 * @param {boolean} [props.requireAll=false] - If true, requires all permissions in the array.
 * @param {React.ReactNode} [props.fallback=null] - Component to render if permission is denied.
 * @param {React.ReactNode} props.children - Component to render if permission is granted.
 */
export function PermissionGuard({
  permission,
  requireAll = false,
  requiredRole = null,
  fallback = null,
  children,
}) {
  const { can, canAny, canAll, isPermissionsLoaded, role } = usePermissions();

  if (!isPermissionsLoaded) {
    return null; // Or a skeleton if we prefer, but usually action buttons shouldn't flash loading states
  }

  if (requiredRole && role !== requiredRole) {
    return <>{fallback}</>;
  }

  if (!permission) {
    return <>{children}</>;
  }

  let hasAccess = false;

  if (Array.isArray(permission)) {
    hasAccess = requireAll ? canAll(permission) : canAny(permission);
  } else {
    hasAccess = can(permission);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}
